/**
 * Central registry of the DEFAULT photos for every editable section on the site.
 *
 * These are the hardcoded fallbacks that render when there is no admin override
 * in the `photo_layouts` MongoDB collection. Keeping them here (instead of
 * inline in each page) lets the Admin > Layout tab preview the current defaults
 * and lets the couple selectively "delete" a default (i.e. save an override
 * that omits it) without touching page code.
 *
 * Everything downstream — Home.jsx, Story.jsx, AdminPhotoLayout.jsx — imports
 * from this single file, so there is exactly one source of truth.
 */

// Base URLs for the two Emergent asset buckets used for wedding photos.
const CUSTOMER = "https://customer-assets.emergentagent.com/job_gallery-story/artifacts/";
const CUSTOMER_JT = "https://customer-assets-jt897jd0.emergentagent.net/job_gallery-story/artifacts/";

// --- Home page defaults ------------------------------------------------------
export const HOME_HERO_DEFAULTS = [
  `${CUSTOMER_JT}nxhkqeke_Wedding%20venue%20bis.jpg`,
];
export const HOME_STORY_PREVIEW_DEFAULTS = [
  `${CUSTOMER_JT}oh2bhfwr_Civil%20partnership%20April%2026%20bis.webp`,
];
export const HOME_TRAVEL_PREVIEW_DEFAULTS = [
  `${CUSTOMER_JT}eqkeswrx_Wedding%20venue.jpg`,
];

// --- Story chapters (index-aligned; 13 entries) ------------------------------
export const STORY_CHAPTER_DEFAULTS = [
  // I — Sept 2022 · Warsaw, a first time
  [`${CUSTOMER_JT}hfoao6us_20241010_132916.jpg`],
  // II — Oct → Dec 2024 · Found again
  [`${CUSTOMER_JT}bl8cf1nk_20241110_033223.jpg`],
  // III — January 2025 · First dates, and a third heartbeat (Max)
  [
    `${CUSTOMER}dvju6ckv_January%202025%20first%20dates%203%20of%20us.jpg`,
    `${CUSTOMER}gxloiz4y_First%20dates%203%20of%20us%20Warsaw%20begining%20of%202025.webp`,
    `${CUSTOMER_JT}in57xt52_20250107_151234.jpg`,
    `${CUSTOMER_JT}ycxyrh7n_20250107_184508.jpg`,
    `${CUSTOMER_JT}wx7rzzl0_20250110_184258.jpg`,
  ],
  // IV — February 2025 · Valentine's + Gdańsk
  [
    `${CUSTOMER_JT}qciji5sh_20250214_185435.jpg`,
    `${CUSTOMER_JT}yzmjggj5_20250214_123845.jpg`,
    `${CUSTOMER}rv24bb7x_First%20trip%20together%20ingdansk%203%20of%20us%20February%202025.jpg`,
    `${CUSTOMER_JT}2wr9nisr_20250215_121226.jpg`,
    `${CUSTOMER_JT}ije8y5fe_20250215_155841.jpg`,
  ],
  // V — March → April 2025 · Warsaw dates continue, a ring, a train seat
  [
    `${CUSTOMER}jogxp44i_May%202025%20eli%20and%20max%20warsaw.jpg`,
    `${CUSTOMER}mov1vt78_May%202025%20dates%20in%20Warsaw%20with%20Max.webp`,
    `${CUSTOMER}37shq7wu_Museum%20in%20Warsaw%203%20of%20us%20May%202025.webp`,
    `${CUSTOMER}aj1jmvho_Family%20in%20Warsaw%20may%2025.jpg`,
  ],
  // VI — May 2025 · Cyprus, first time as three
  [
    `${CUSTOMER_JT}cuo6crtm_20250509_180629.jpg`,
    `${CUSTOMER_JT}cjqm8ual_20250522_151531.jpg`,
    `${CUSTOMER_JT}fwng385k_20250522_170719.jpg`,
    `${CUSTOMER}4jjq1lwr_May%202025%20mag%20visits%20eli%20in%20cyprus.jpg`,
    `${CUSTOMER_JT}w3mio77h_20250522_205636.jpg`,
  ],
  // VII — 9 June 2025 · A yes, at a small table in Warsaw
  [
    `${CUSTOMER}iljo90gn_Dates%20in%20Warsaw%20June%202026.jpg`,
    `${CUSTOMER}8i45tq3q_Proposal%20at%20restaurant%20June%202025.jpg`,
    `${CUSTOMER}9zqknn5n_Eli%20and%20mag%20Warsaw%20June%202025.jpg`,
    `${CUSTOMER}95hawzhb_June%202025%20warsaw.jpg`,
    `${CUSTOMER}4yjxoe2j_June%202025.jpg`,
    `${CUSTOMER_JT}urfjcsn0_20250608_212855.jpg`,
    `${CUSTOMER_JT}6pwkgzug_IMG-20250609-WA0143.jpg`,
    `${CUSTOMER_JT}x9etks2w_IMG-20250609-WA0045.jpg`,
    `${CUSTOMER_JT}rb2l4irs_IMG-20250609-WA0040.jpg`,
  ],
  // VIII — July 2025 · The move to Cyprus
  [
    `${CUSTOMER_JT}3o5lbeg6_20250709_162751.jpg`,
    `${CUSTOMER_JT}ipyt74i1_20250725_144559.jpg`,
  ],
  // IX — Jul → Sep 2025 · Romania, Malta, day trips
  [
    `${CUSTOMER}lgcdxv1o_Family%20goes%20to%20Malta%20to%20visit%20eli%200925.jpg`,
    `${CUSTOMER}21cdgwdb_Max%20and%20Eli%20in%20Malta%200925%20bis.webp`,
    `${CUSTOMER}gnxkls3m_Danuta%2C%20Mag%27s%20witness%20visiting%20the%20wedding%20venue.jpg`,
    `${CUSTOMER}kqmlwaix_Family%20trip%20to%20park%20sept%2025.jpg`,
  ],
  // X — October 2025 · Free and a Disneyland sky
  [
    `${CUSTOMER}d8z08r94_Eli%20and%20Mag%20Disneyland%20oct%2025.jpg`,
    `${CUSTOMER}j6cz452e_Family%20trip%20Oct%2025%20bis.jpg`,
    `${CUSTOMER_JT}04bb3yuf_20251008_133223.jpg`,
    `${CUSTOMER_JT}0n3i4up7_20251012_185357.jpg`,
    `${CUSTOMER_JT}qp2aqfu2_20251026_122134.jpg`,
  ],
  // XI — Dec 2025 → Jan 2026 · First Christmas
  [
    `${CUSTOMER_JT}k7lerqju_IMG-20251207-WA0033.jpg`,
    `${CUSTOMER}qermi373_Christmas%20in%20Cyprus%201225%20eli%20and%20mag.jpg`,
    `${CUSTOMER_JT}m2ylf3xe_20251227_092611.jpg`,
    `${CUSTOMER}b06biwkk_Eli%20and%20Max%20camper%20trip%20dec%202025.jpg`,
    `${CUSTOMER}4ks1ai9u_Eli%20and%20MAG%20date%20in%20Paris%20Jan%2026.jpg`,
  ],
  // XII — Feb → Mar 2026 · Armenia, hearts, and Cyprus mountains
  [
    `${CUSTOMER_JT}gx9mzc74_IMG-20260215-WA0221.jpg`,
    `${CUSTOMER_JT}x8yo40ja_IMG-20260216-WA0144%281%29.jpg`,
    `${CUSTOMER}vs2kc5x2_Eli%20and%20Mag%20in%20Cyprus%20mountain%20trip%20January%2026.jpg`,
    `${CUSTOMER_JT}tbgn7onw_IMG-20260303-WA0048.jpg`,
  ],
  // XIII — 15 April 2026 · Civil union + the slow good life
  [
    `${CUSTOMER}oh2bhfwr_Civil%20partnership%20April%2026%20bis.webp`,
    `${CUSTOMER}icotucju_Civil%20partnership%20April%2026.webp`,
    `${CUSTOMER}vh69hqiu_Civil%20partnership%20April%202026%204.jpg`,
    `${CUSTOMER}mktqgcqw_Eli%20and%20Mag%20in%20Cyprus%20may%2026.jpg`,
    `${CUSTOMER}3jdp238m_Eli%20and%20Mag%20in%20Cyprus%20may%2026%20ter.webp`,
    `${CUSTOMER}y5xdl1r1_Eli%20and%20Mag%20May%2026%20cyprus.jpg`,
    `${CUSTOMER}l75vr79t_Family%20in%20Cyprus%20May%202026.jpg`,
    `${CUSTOMER}5s0du9bd_Family%20in%20Cyprus%20summer%202026.jpg`,
    `${CUSTOMER}vocpwwk4_Eli%20and%20Max%20in%20Cyprus%20may%2026.jpg`,
    `${CUSTOMER_JT}ysb2cru4_20260509_173845.jpg`,
  ],
];

// --- Wedding-venue atmospheric trio ------------------------------------------
export const STORY_VENUE_DEFAULTS = [
  `${CUSTOMER}eqkeswrx_Wedding%20venue.jpg`,
  `${CUSTOMER}nxhkqeke_Wedding%20venue%20bis.jpg`,
  `${CUSTOMER}it67eqqg_Wedding%20venue%203.jpg`,
];

/**
 * Section-key → default URLs map. Consumed by AdminPhotoLayout to show the
 * defaults as editable tiles when no override is stored yet.
 */
export const SECTION_DEFAULTS = {
  "home.hero": HOME_HERO_DEFAULTS,
  "home.storyPreview": HOME_STORY_PREVIEW_DEFAULTS,
  "home.travelPreview": HOME_TRAVEL_PREVIEW_DEFAULTS,
  "story.venue": STORY_VENUE_DEFAULTS,
  ...Object.fromEntries(
    STORY_CHAPTER_DEFAULTS.map((arr, i) => [`story.beats.${i}`, arr]),
  ),
};
